import { Drawer, PathBuilder, Point } from "@common/components/Rendering";
import { ArrowId, buckling_line_syle, meassure_text_style } from "../ViewportStyles";

interface BucklingValue {
  value: number;
  at: number;
}
import { IDisplacementDictionary } from "@common/types/sharedTypes";

export const create_buckling_data = (auto: boolean, height: number, mode: number[], isBottomFree = false): BucklingValue[] => {
  const ret = new Array<BucklingValue>();
  const numOfElems = 20; // this is decided in "analysis_beam_api"
  const numOfNodes = numOfElems + 1;

  if (mode && auto) {
    if (mode.length >= numOfElems - 1) {
      // The analysis returns values for internal nodes only (elements 1-20, never node 0).
      // hasBottomNode is a forward-compat guard in case the API ever includes the bottom node.
      // For a free bottom (rigid_roller), rotZ=-1 means dv/dx=0 at x=0 (zero slope).
      // Quadratic extrapolation with zero-slope constraint: v(0) = (4·v₁ − v₂) / 3
      const hasBottomNode = mode.length > numOfElems;
      const modeOffset = hasBottomNode ? 1 : 0;

      let bottomValue: number;
      if (hasBottomNode) {
        bottomValue = -Math.abs(mode[0]);
      } else if (isBottomFree && mode.length >= 2) {
        bottomValue = -Math.abs((4 * mode[0] - mode[1]) / 3);
      } else {
        bottomValue = 0.0;
      }

      ret.push({ value: bottomValue, at: 0.0 });

      const modeCount = mode.length - modeOffset;
      let it = 1;
      for (; it <= modeCount; it += 1) {
        const f = it / numOfElems;
        ret.push({
          value: -Math.abs(mode[it - 1 + modeOffset]),
          at: f * height,
        });
      }

      if (it < numOfNodes) {
        ret.push({ value: 0.0, at: height });
      }
    }
  } else {
    const amplitude: number = 1;
    const invert: boolean = true;
    for (let it = 0; it < numOfNodes; it += 1) {
      const f = it / numOfElems;
      const x = f * 2 - 1; // maps 0..1 -> -1..1
      const value = amplitude * (1 - x * x); // parabola: 0 at edges, amplitude at center
      ret.push({ value: invert ? -value : value, at: f * height });
    }
  }
  return ret;
};

export const draw_buckling_data = (
  g: Drawer<ArrowId>,
  sett: { column_height: number; center_line_hor: number; mode: number[]; is_visible: boolean; isBottomFree?: boolean },
  auto: boolean,
  betas: number[],
) => {
  const { column_height, center_line_hor, mode, isBottomFree = false } = sett;

  const buckling_data = create_buckling_data(auto, column_height, mode, isBottomFree);

  const buckling_hor_dist = 0.5; // how many meters the buckling is allowed to scale from it's origin
  const max_buckling_value = Math.max(...buckling_data.map((x) => Math.abs(x.value)));
  const buckling_hor = center_line_hor;
  const buckling_ver = 0;
  const buckling_path = new PathBuilder();
  {
    let first = true;
    for (const buck of buckling_data) {
      const val = buck.value;
      const p: Point = [buckling_hor - (val / max_buckling_value) * buckling_hor_dist, buckling_ver - buck.at];
      if (first) buckling_path.moveTo(p);
      else buckling_path.drawLineTo(p);
      first = false;
    }
  }

  g.drawPath(buckling_line_syle, buckling_path, {
    display: sett.is_visible,
  });

  if (betas !== undefined && betas.length > 0 && betas[0] !== undefined && betas[0] !== null) {
    g.drawText(
      [buckling_hor_dist, -column_height / 2],
      "β = " + betas[0].toFixed(2),
      {
        lineColor: "var(--sx-viewport-black)",
        textSize: 12,
      },
      {
        display: sett.is_visible,
        rotation: "upward",
        horiAlign: "left",
        vertOffset: 0,
        horiOffset: -30,
      },
    );
  }
};
